import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

// Shared UI components used across the project
import FormContainer from "../../components/CommonComponents/FormContainer";
import LeftWrapper from "../../components/CommonComponents/LeftWrapper";
import RightWrapper from "../../components/CommonComponents/RightWrapper";
import Wrapper from "../../components/CommonComponents/Wrapper";
import AdminSaveHeader from "../../components/CommonComponents/AdminSaveHeader";
import AdminBackNavigation from "../../components/CommonComponents/AdminBackNavigation";
import MessageLabel from "../../components/CommonComponents/MessageLabel";
import MessageSubHeading from "../../components/CommonComponents/MessageSubHeading";
import AdminCustomInput from "../../components/CommonComponents/AdminCustomInput";
import AdminSwitch from "../../components/CommonComponents/AdminSwitch";
import Defaultloadinggif from "../../layout/Others/Defaultloadinggif";
import Response_Processing from "../../layout/Others/Response_Processing";

import { getItemToken } from "../../Utils/localStorageUtils";
import { slugGenerator } from "../../components/CommonMethod/CommonMethods";

const OrientationAddUpdate = () => {
  const baseURL = process.env.REACT_APP_Baseurl;
  const headers = getItemToken("access_token");

  // Orientation endpoints
  const createApi = `${baseURL}/admin/orientation/create`;
  const editApi = `${baseURL}/admin/orientation/edit`;
  const storeApi = `${baseURL}/admin/orientation/store`;
  const updateApi = `${baseURL}/admin/orientation/update`;

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [showOverlay, setShowOverlay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    images_size: "",
    image_ratio: "",
    active: 0,
  });
  const [formDataError, setFormDataError] = useState({});

  const combinedRef = useRef({ name: null, slug: null, active: null });

  const focusInput = (inputName) => {
    if (combinedRef.current[inputName]) {
      combinedRef.current[inputName].focus();
    }
  };

  // Fetch for edit
  const fetchEditData = async () => {
    if (!id) return;
    setShowOverlay(true);
    try {
      const response = await axios.get(`${editApi}/${id}`, { headers });
      const result = response?.data;
      // Expecting { status: true, data: { name, slug, images_size, active } }
      if (result?.status && result?.data) {
        setFormData({
          name: result.data.name || "",
          slug: result.data.slug || "",
          images_size: result.data.images_size || "",
          image_ratio: result.data.image_ratio || "",
          active: result.data.active ?? 0,
        });
      } else {
        toast.error(result?.message || "Failed to load orientation.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error fetching orientation.");
    } finally {
      setShowOverlay(false);
    }
  };

  // Fetch defaults for create (if needed)
  const fetchCreateData = async () => {
    if (id) return;
    setShowOverlay(true);
    try {
      // This can be a no-op for now since it's a dummy
      await axios.get(`${createApi}`, { headers });
    } catch (_err) {
      // ignore
    } finally {
      setShowOverlay(false);
    }
  };

  useEffect(() => {
    if (id) fetchEditData();
    else fetchCreateData();
  }, [id, location]);

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
      setFormDataError((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormDataError((prev) => ({ ...prev, [name]: "" }));

    // Auto-calculate image ratio when image dimension changes
    if (name === "images_size") {
      const ratio = calculateImageRatio(value);
      setFormData((prev) => ({ ...prev, [name]: value, image_ratio: ratio }));
    }
  };

  const calculateImageRatio = (dimension) => {
    if (!dimension || typeof dimension !== "string") return "";
    const parts = dimension.toLowerCase().split("x");
    if (parts.length !== 2) return "";
    const w = parseInt(parts[0].trim(), 10);
    const h = parseInt(parts[1].trim(), 10);
    if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return "";
    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
    const d = gcd(w, h);
    return `${w / d}:${h / d}`;
  };

  const generateSlugIfEmpty = () => {
    if (!formData?.slug) {
      const slug = slugGenerator(formData?.name || "");
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleNameKeyDown = (event) => {
    if (event.key === "Enter" || event.key === "Tab") {
      generateSlugIfEmpty();
    }
  };

  const validate = () => {
    let valid = true;
    if (!formData?.name || formData?.name?.trim() === "") {
      setFormDataError((prev) => ({ ...prev, name: "Name cannot be empty." }));
      focusInput("name");
      valid = false;
    }
    if (!formData?.slug || formData?.slug?.trim() === "") {
      setFormDataError((prev) => ({ ...prev, slug: "Slug cannot be empty." }));
      if (valid) focusInput("slug");
      valid = false;
    }
    if (
      formData?.name &&
      (formData?.name?.trim().length < 2 || formData?.name?.trim().length > 100)
    ) {
      toast.error("Name must be between 2 and 100 characters.");
      if (valid) focusInput("name");
      valid = false;
    }
    if (
      formData?.slug &&
      (formData?.slug?.trim().length < 2 || formData?.slug?.trim().length > 100)
    ) {
      toast.error("Slug must be between 2 and 100 characters.");
      if (valid) focusInput("slug");
      valid = false;
    }
    return valid;
  };

  const handleStore = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        name: formData?.name,
        slug: formData?.slug,
        images_size: (formData?.images_size ?? '').toString().trim(),
        image_ratio: formData?.image_ratio || '',
        active: formData?.active,
      };
      if (!id) {
        const response = await axios.post(`${storeApi}`, payload, { headers });
        const result = response?.data;
        if (result?.status) {
          toast.success(result?.message || "Orientation created successfully");
          navigate("/orientation-list");
        } else {
          toast.error(result?.message || "Failed to create orientation");
        }
      } else {
        const response = await axios.patch(`${updateApi}/${id}`, payload, { headers });
        const result = response?.data;
        if (result?.status) {
          toast.success(result?.message || "Orientation updated successfully");
        } else {
          toast.error(result?.message || "Failed to update orientation");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  if (showOverlay) {
    return <Defaultloadinggif className="heightOnLoader" />;
  }

  return (
    <React.Fragment>
      {showOverlay && <Response_Processing />}
      <AdminBackNavigation path="/orientation-list" text="Back" />
      <AdminSaveHeader
        heading={!id ? "Add Orientation" : "Update Orientation"}
        saveText={!id ? "Save" : "Update"}
        saveMethod={handleStore}
        id={"orientation-save-button"}
      />
      <FormContainer className="pb-5">
        <LeftWrapper>
          <Wrapper>
            <MessageLabel text="Name" noMargin className="mandatorySimple" />
            <AdminCustomInput
              id="orientation-name"
              type="text"
              name="name"
              value={formData?.name}
              onChange={handleInput}
              onBlur={generateSlugIfEmpty}
              onKeyDown={handleNameKeyDown}
              placeHolder="Name"
              ref={(el) => (combinedRef.current.name = el)}
              error={formDataError?.name}
            />

            <MessageLabel text="Slug" noMargin className="mandatorySimple" />
            <AdminCustomInput
              id="orientation-slug"
              type="text"
              name="slug"
              value={formData?.slug}
              onChange={handleInput}
              placeHolder="Slug"
              ref={(el) => (combinedRef.current.slug = el)}
              error={formDataError?.slug}
            />
          </Wrapper>
        </LeftWrapper>
        <RightWrapper>
          <Wrapper>
            <MessageLabel text="Image Dimension" noMargin />
            <AdminCustomInput
              id="orientation-images-size"
              type="text"
              name="images_size"
              value={formData?.images_size}
              onChange={handleInput}
              placeHolder="e.g., 1080x1920"
              ref={(el) => (combinedRef.current.images_size = el)}
              error={formDataError?.images_size}
            />
            <MessageLabel text="Image Ratio" noMargin />
            <AdminCustomInput
              id="orientation-image-ratio"
              type="text"
              name="image_ratio"
              value={formData?.image_ratio}
              placeHolder="Auto-calculated"
              readOnly
              disabled
            />
            <div className="d-flex justify-content-between pt-2 mb-2 ">
              <div className="col-6 col-md-6 pe-2">
                <MessageLabel text="Active" />
              </div>
              <div className="col-6 col-md-6 ps-2 text-end">
                <AdminSwitch id="orientation-active" name={'active'} checked={formData?.active == 1}
                  onChange={(e) =>
                    handleInput({
                      target: {
                        name: "active",
                        type: "checkbox",
                        checked: e.target.checked,
                      },
                    })
                  }
                  ref={(el) => (combinedRef.current.active = el)}
                />
              </div>
            </div>
          </Wrapper>
        </RightWrapper>
      </FormContainer>
    </React.Fragment>
  );
};

export default OrientationAddUpdate;

